import { useCallback } from 'react'
import type { MutableRefObject, Ref } from 'react'

/**
 * Encaminha um mesmo nó para vários refs.
 *
 * Os controles da camada precisam disto porque recebem dois ou três refs: o do
 * `register` do react-hook-form, o do `Field.Control` — que é um `Slot` e
 * injeta `id` e os atributos ARIA no filho — e, nos campos mascarados, o que
 * aplica a máscara. Ficar com apenas um deles quebra o formulário ou a
 * acessibilidade, e o sintoma de cada caso é distante o bastante da causa para
 * custar caro.
 *
 * `node` é `T | null` porque o React passa `null` na desmontagem. Tipar como
 * não-nulo faria a função gravar `null` num ref declarado como sempre
 * preenchido.
 */
export function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') ref(node)
      else if (ref && typeof ref === 'object') {
        ;(ref as MutableRefObject<T | null>).current = node
      }
    })
  }
}

/**
 * Versão memoizada, para usar em render.
 *
 * `mergeRefs` devolve uma função nova a cada chamada. Passada direto no JSX,
 * ela muda de identidade a cada render e o React desanexa e reanexa o ref —
 * o que, num campo mascarado, reaplica a máscara toda vez. Em formulário com
 * `watch` ou erros, isso acontece a cada tecla.
 */
export function useMergedRef<T>(...refs: Array<Ref<T> | undefined>) {
  // Os refs são posicionais e estáveis por construção nos controles desta
  // camada; memoizar pela lista evita recriar o callback a cada render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(mergeRefs(...refs), refs)
}
